import { useEffect, useState, type RefObject } from 'react';
import { m2t, plural } from '../../lib/ui';
import { locateHour, locateSpan, type HeatCell, type HeatModel, type HeatPost } from './derive';

/**
 * What follows the pointer over the heatmap: the breakdown tooltip and the hour
 * highlight through every day band. It lives apart from the grid and listens on
 * the grid's DOM, so moving across hundreds of cells re-renders this and never
 * the cells themselves.
 */

type Hover = { el: HTMLElement; x: number; y: number };

export const trackX = (i: number, n: number) =>
  `calc(var(--heat-label) + (100% - var(--heat-label) - var(--heat-sum)) * ${i} / ${n})`;
export const trackW = (n: number) => `calc((100% - var(--heat-label) - var(--heat-sum)) / ${n})`;

const STATE_LABEL: Record<string, string> = {
  gap: 'Vrzel — nihče ni dodeljen',
  rejected: 'Zavrnjeno',
  pending: 'Čaka potrditev',
  ok: 'Potrjeno',
  external: 'Zunanja ekipa',
};

const SWATCH: Record<string, string> = {
  short: 'var(--heat-r3)',
  pending: 'var(--heat-a4)',
  neutral: 'var(--heat-bar-neutral)',
  ok: 'var(--heat-bar-ok)',
};

export function HeatOverlay({
  gridRef,
  model,
  flash,
}: {
  gridRef: RefObject<HTMLDivElement | null>;
  model: HeatModel;
  /** The hour a hotspot chip just pointed at; `n` changes on every click. */
  flash: { i: number; n: number } | null;
}) {
  const [hover, setHover] = useState<Hover | null>(null);
  const [flashing, setFlashing] = useState<number | null>(null);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const onMove = (ev: PointerEvent) => {
      const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-tip]');
      setHover(el ? { el, x: ev.clientX, y: ev.clientY } : null);
    };
    const onLeave = () => setHover(null);
    const onFocus = (ev: FocusEvent) => {
      const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-tip]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHover({ el, x: r.left + r.width / 2, y: r.bottom });
    };
    grid.addEventListener('pointermove', onMove);
    grid.addEventListener('pointerleave', onLeave);
    grid.addEventListener('focusin', onFocus);
    grid.addEventListener('focusout', onLeave);
    return () => {
      grid.removeEventListener('pointermove', onMove);
      grid.removeEventListener('pointerleave', onLeave);
      grid.removeEventListener('focusin', onFocus);
      grid.removeEventListener('focusout', onLeave);
    };
  }, [gridRef]);

  useEffect(() => {
    if (!flash) return;
    setFlashing(flash.i);
    const t = window.setTimeout(() => setFlashing(null), 1400);
    return () => window.clearTimeout(t);
  }, [flash]);

  // The model a hover was taken against may have been rebuilt since.
  const content = hover && hover.el.isConnected ? tipContent(hover.el, model) : null;
  const n = model.hours.length;
  const hoverI = content?.i ?? null;
  const bandI = flashing ?? hoverI;

  return (
    <>
      {bandI !== null ? (
        <div
          className={`rr-heat-track-x rr-heat-colband${flashing !== null ? ' rr-heat-colband--flash' : ''}`}
          style={{ left: trackX(bandI, n), width: trackW(n) }}
        />
      ) : null}
      {content && hover ? <Tip x={hover.x} y={hover.y} content={content} /> : null}
    </>
  );
}

interface TipContent {
  i: number | null;
  title: string;
  sub: string;
  rows: { swatch: string; label: string; note?: string; value?: number }[];
  hint?: string;
}

function Tip({ x, y, content }: { x: number; y: number; content: TipContent }) {
  // Flip to the other side of the pointer near the viewport's edges.
  const left = x + 300 > window.innerWidth ? Math.max(8, x - 300) : x + 14;
  const top = y + 150 > window.innerHeight ? Math.max(8, y - 150) : y + 16;
  return (
    <div className="rr-heat-tip" style={{ left, top }} role="tooltip">
      <div className="rr-heat-tip-title">{content.title}</div>
      <div className="rr-heat-tip-sub">{content.sub}</div>
      {content.rows.map((r) => (
        <div key={r.label} className="rr-heat-tip-row">
          <i style={{ background: r.swatch }} />
          <span>
            {r.label}
            {r.note ? <small> {r.note}</small> : null}
          </span>
          <b>{r.value ?? ''}</b>
        </div>
      ))}
      {content.hint ? <div className="rr-heat-tip-hint">{content.hint}</div> : null}
    </div>
  );
}

const openLabel = (n: number) =>
  `${n} ${plural(n, 'odprto mesto', 'odprti mesti', 'odprta mesta', 'odprtih mest')}`;

function countRows(c: HeatCell, posts: HeatPost[], i: number): TipContent['rows'] {
  const hour = c.hour;
  let rejected = 0;
  for (const p of posts) {
    if (p.marks[i] !== 'short') continue;
    const touching = p.coverage.filter((s) => s.fromMin < (hour + 1) * 60 && s.toMin > hour * 60);
    if (!touching.some((s) => s.state === 'gap')) rejected++;
  }
  const rows: TipContent['rows'] = [
    {
      swatch: SWATCH.short!,
      label: 'Manjka oseba',
      note: c.short && rejected ? `vrzel ${c.short - rejected} · zavrnjeno ${rejected}` : undefined,
      value: c.short,
    },
    { swatch: SWATCH.pending!, label: 'Čaka potrditev', value: c.pending },
  ];
  if (c.neutral) rows.push({ swatch: SWATCH.neutral!, label: 'Skrito v legendi', value: c.neutral });
  rows.push({ swatch: SWATCH.ok!, label: 'Brez težav', value: c.ok });
  return rows;
}

function tipContent(el: HTMLElement, model: HeatModel): TipContent | null {
  // Read during a render the new model may already drive, before React has
  // rewritten the element's attributes: look everything up again by hour and
  // start minute, and show nothing for what is no longer there.
  const { tip, day: dayId = '', sector: sectorKey = '', h, post: postId = '', from } = el.dataset;

  if (tip === 'span') {
    const found = locateSpan(model, dayId, sectorKey, postId, Number(from));
    if (!found) return null;
    const { day, post, span } = found;
    return {
      i: null,
      title: `${post.code} · ${post.micro}`,
      sub: `${day.label} · ${m2t(span.fromMin)}–${m2t(span.toMin)}`,
      rows: [
        {
          swatch:
            span.state === 'gap' || span.state === 'rejected'
              ? SWATCH.short!
              : span.state === 'pending'
                ? SWATCH.pending!
                : SWATCH.ok!,
          label: STATE_LABEL[span.state]!,
        },
      ],
      hint: span.name ?? undefined,
    };
  }

  if (h === undefined) return null;
  if (tip === 'bar') {
    const found = locateHour(model, dayId, Number(h));
    if (!found) return null;
    const { day, index: i, cell: c } = found;
    const posts = day.allSectors.flatMap((s) => s.posts);
    return {
      i,
      title: `${day.label} · vsi sektorji`,
      sub: `${c.hour}:00–${c.hour + 1}:00 · ${openLabel(c.open)}`,
      rows: countRows(c, posts, i),
      hint: c.open ? 'Klik: po sektorjih' : undefined,
    };
  }
  const found = locateHour(model, dayId, Number(h), sectorKey);
  if (!found?.sector) return null;
  const { day, sector, index: i, cell: c } = found;
  if (!c.open) return { i, title: `${day.label} · ${sector.name}`, sub: `${c.hour}:00–${c.hour + 1}:00 · ni odprtih mest`, rows: [] };
  return {
    i,
    title: `${day.label} · ${sector.name}`,
    sub: `${c.hour}:00–${c.hour + 1}:00 · ${openLabel(c.open)}`,
    rows: countRows(c, sector.posts, i),
    hint: c.short || c.pending ? 'Klik: seznam mest in dejanja' : undefined,
  };
}
